import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Paper, Grid, TextField, Autocomplete, Typography, Button, Table, TableHead, TableBody, TableRow, TableCell, TableContainer, IconButton, MenuItem, List, ListItem, ListItemText, Divider, TablePagination, Drawer, Card, CardContent, useTheme, useMediaQuery } from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import CancelIcon from '@mui/icons-material/Cancel';
import apiService from '../../services/apiService';
import { getCurrentUser } from '../../services/authService';
import { format } from 'date-fns';
import BillPrintDialog from './BillPrintDialog'; // Import the BillPrintDialog
import PrintIcon from '@mui/icons-material/Print'; // Import PrintIcon
import CheckCircleIcon from '@mui/icons-material/CheckCircle'; // Import CheckCircleIcon
import AddCustomerDialog from './AddCustomerDialog';
import AddProductDialog from './AddProductDialog';

const filter = createFilterOptions();

const CustomerBill = () => {
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerInput, setCustomerInput] = useState('');
  const [customerBalance, setCustomerBalance] = useState('0'); // For top card - overall balance
  const [transactionOldBalance, setTransactionOldBalance] = useState('0'); // For bottom card - transaction's starting balance
  const [currentBillSummaryId, setCurrentBillSummaryId] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  const [salesList, setSalesList] = useState([]);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);
  const [totalElements, setTotalElements] = useState(0);
  const [rawActiveBills, setRawActiveBills] = useState([]); // State for raw API data
  const [activeBills, setActiveBills] = useState([]); // State for mapped data with names

  const [form, setForm] = useState({
    salesDetailId: undefined,
    product: null,
    productInput: '',
    actualPrice: '',
    salesPrice: '',
    quantity: '',
    price: '',
    originalPrice: 0,
  });
  const [saving, setSaving] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [receivedToday, setReceivedToday] = useState('');
  const customerInputRef = useRef(null);
  const productInputRef = useRef(null);
  const salesPriceInputRef = useRef(null);
  const quantityInputRef = useRef(null);
  const user = getCurrentUser();
  
  // State for print dialog
  const [isBillPrintDialogOpen, setBillPrintDialogOpen] = useState(false);
  const [selectedBillToPrint, setSelectedBillToPrint] = useState(null);
  const [selectedBillDetailsToPrint, setSelectedBillDetailsToPrint] = useState([]);

  // State for Add Customer Dialog
  const [addCustomerDialogOpen, setAddCustomerDialogOpen] = useState(false);
  // State for Add Product Dialog
  const [addProductDialogOpen, setAddProductDialogOpen] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Corrected and simplified role checking logic
  const rolesRaw = user?.roles || user?.authorities || user?.permissions || user?.role || [];
  const roles = (Array.isArray(rolesRaw) ? rolesRaw : [rolesRaw])
    .map(r => String(r?.authority || r || '').toLowerCase());
  const canDelete = roles.some(role => role === 'admin' || role === 'super_user' || role === 'role_admin' || role === 'role_super_user');

  const loadCustomers = useCallback(async () => {
    try {
      const resp = await apiService.customers.getMinDetails();
      setCustomers(Array.isArray(resp.data) ? resp.data : []);
    } catch (e) {
      setCustomers([]);
    }
  }, []);

  const loadProducts = useCallback(async () => {
    try {
      const resp = await apiService.products.list({ page: 0, size: 500 });
      const content = resp.data?.content || resp.data || [];
      setProducts(Array.isArray(content) ? content : []);
    } catch (e) {
      setProducts([]);
    }
  }, []);

  const refreshBalance = useCallback(async (cust) => {
    if (!cust?.customerId) { setCustomerBalance('0'); return '0'; }
    try {
      const resp = await apiService.customers.getBalance(cust.customerId);
      const bal = resp?.data?.cumulativeBalance ?? resp?.data?.oldBalance ?? 0;
      setCustomerBalance(String(bal));
      return String(bal);
    } catch (e) {
      const fallbackBal = String(cust?.balanceAmount ?? 0);
      setCustomerBalance(fallbackBal);
      return fallbackBal;
    }
  }, []);

  const fetchSales = useCallback(async (billSummaryId) => {
    if (!billSummaryId) {
      setSalesList([]);
      setTotalElements(0);
      return;
    }
    try {
      // Fetch ALL items (no pagination params)
      const resp = await apiService.salesDetails.listByCustomerAndDate({
        billSummaryId,
        date: format(new Date(), 'yyyy-MM-dd'),
        sortBy: 'updateTimestamp',
        direction: 'ASC' // Fetch oldest first
      });

      // The backend now returns a simple array
      const content = Array.isArray(resp.data) ? resp.data : (resp.data?.content || []);

      // Reverse for display (newest on top)
      setSalesList(content.reverse());
      setTotalElements(content.length);
    } catch (e) {
      setSalesList([]);
      setTotalElements(0);
    }
  }, []);

  // Fetches raw active bills without customer names
  const loadActiveBills = useCallback(async () => {
    try {
      const today = format(new Date(), 'yyyy-MM-dd');
      const resp = await apiService.collections.getActiveBillsToday({ date: today, billTypeFlag: 'C' });
      setRawActiveBills(Array.isArray(resp.data) ? resp.data : []);
    } catch (e) {
      setRawActiveBills([]);
    }
  }, []);

  // Maps customer names to raw bills when either customers or raw bills change
  useEffect(() => {
    if (customers.length > 0) {
      const byId = new Map(customers.map(c => [c.customerId, c]));
      const mapped = rawActiveBills.map(it => ({
        ...it,
        customerName: it.customerNameTamil || it.customerName || byId.get(it.customerId)?.customerNameTamil || byId.get(it.customerId)?.customerName || String(it.customerId),
        grandTotal: (Number(it.billAmount) || 0) + (Number(it.oldBalance) || 0),
      }));
      setActiveBills(mapped);
    } else {
      // Fallback if customers haven't loaded yet
      const mapped = rawActiveBills.map(it => ({
        ...it,
        grandTotal: (Number(it.billAmount) || 0) + (Number(it.oldBalance) || 0),
      }));
      setActiveBills(mapped);
    }
  }, [rawActiveBills, customers]);

  const findTodayBillSummaryForCustomer = useCallback(async (cust, specificBillSummaryId) => {
    try {
      const today = format(new Date(), 'yyyy-MM-dd');
      const resp = await apiService.collections.getTodayCollections({ date: today, billTypeFlag: 'C' });
      const list = Array.isArray(resp?.data) ? resp.data : [];
      const entry = specificBillSummaryId
        ? list.find(r => r.billSummaryId === specificBillSummaryId)
        : list.find(r => r.customerId === cust?.customerId);
      return entry || null;
    } catch (e) {
      return null;
    }
  }, []);

  useEffect(() => {
    loadCustomers();
    loadProducts();
    loadActiveBills(); // Initial load
  }, [loadCustomers, loadProducts, loadActiveBills]);

  // Trigger fetchSales when currentBillSummaryId changes
  useEffect(() => {
    if (currentBillSummaryId) {
      fetchSales(currentBillSummaryId);
    }
  }, [currentBillSummaryId, fetchSales]);

  useEffect(() => {
    if (customerInputRef.current) customerInputRef.current.focus();
  }, []);

  const onCustomerChange = (_e, val) => {
    if (val?.isAddNew) {
      setAddCustomerDialogOpen(true);
      return;
    }
    setSalesList([]); // Clear previous sales list immediately
    setSelectedCustomer(val || null);
    setPage(0);
    if (val) {
      (async () => {
        const overallBalance = await refreshBalance(val); // Always fetch overall balance for top card
        const entry = await findTodayBillSummaryForCustomer(val);
        if (entry && entry.isPaid !== 'Y') {
          setCurrentBillSummaryId(entry.billSummaryId);
          setReceivedToday(String(entry.receivedAmount || ''));
          setTransactionOldBalance(String(entry.oldBalance || '0'));
          // fetchSales is triggered by useEffect
        } else {
          // No existing bill, prepare for a new one
          setCurrentBillSummaryId(null);
          setReceivedToday('');
          setTransactionOldBalance(overallBalance); // For a new bill, the transaction's old balance IS the customer's current overall balance
        }
      })();
      setTimeout(() => {
        if (productInputRef.current) {
          productInputRef.current.focus();
        }
      }, 100);
    } else {
      // Clearing the selection
      setCurrentBillSummaryId(null);
      setReceivedToday('');
      setCustomerBalance('0');
      setTransactionOldBalance('0');
    }
  };

  const handleSaveNewCustomer = async (newCustomer) => {
    // Refresh customer list
    await loadCustomers();

    // Select the new customer
    setSelectedCustomer(newCustomer);
    setCustomerInput(''); // Clear input value so the selected customer displays correctly
    setCustomerBalance('0'); // New customer starts with 0 balance
    setTransactionOldBalance('0');
    setCurrentBillSummaryId(null);
    setReceivedToday('');
    setSalesList([]);

    setAddCustomerDialogOpen(false);

    // Focus on Product input
    requestAnimationFrame(() => {
        productInputRef.current?.focus();
    });
  };

  const saveReceived = async () => {
    if (!selectedCustomer?.customerId || !currentBillSummaryId) {
      alert('Select a customer and add at least one item before receiving amount');
      return;
    }
    if (Number(receivedToday || 0) <= 0) {
      alert('Received amount must be greater than 0.');
      return;
    }
    try {
      setSaving(true);
      const payload = {
        billSummaryId: currentBillSummaryId,
        receivedAmount: receivedToday,
      };
      await apiService.customerBill.receivePayment(payload);
      
      await loadActiveBills();

      // Reset all customer-specific state to prepare for the next entry
      setSelectedCustomer(null);
      setCustomerInput('');
      setCurrentBillSummaryId(null);
      setSalesList([]);
      setCustomerBalance('0');
      setTransactionOldBalance('0');
      setReceivedToday('');

      // Focus the customer input for the next entry
      setTimeout(() => {
        customerInputRef.current?.focus();
      }, 0);

    } catch (e) {
      alert('Failed to save received amount');
    } finally {
      setSaving(false);
    }
  };

  const currentActiveBill = useMemo(() => {
    if (!selectedCustomer || !currentBillSummaryId) return null;
    return activeBills.find(b => b.billSummaryId === currentBillSummaryId);
  }, [activeBills, selectedCustomer, currentBillSummaryId]);

  const totals = useMemo(() => {
    const sum = currentActiveBill
      ? (Number(currentActiveBill.billAmount) || 0)
      : (salesList || []).reduce((acc, r) => acc + (Number(r.price || 0) || 0), 0);

    const oldBal = Number(transactionOldBalance || 0) || 0;
    const grand = sum + oldBal;
    const rec = Number(receivedToday || 0) || 0;
    const bal = Math.max(0, grand - rec);
    return { total: sum, oldBal, grandTotal: grand, received: rec, balance: bal };
  }, [salesList, transactionOldBalance, receivedToday, currentActiveBill]);

  const deleteRow = async (row) => {
    if (!canDelete) return;
    if (!row?.salesDetailId) {
      alert('Cannot delete this item: Missing required data');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    try {
      await apiService.salesDetails.delete(row.salesDetailId);
      await fetchSales(currentBillSummaryId);
      await loadActiveBills();
    } catch (error) {
      console.error('Failed to delete item:', error);
      alert('Failed to delete item');
    }
  };

  const onProductChange = (_e, val) => {
    if (val?.isAddNew) {
      setAddProductDialogOpen(true);
      return;
    }
    setForm((prev) => {
      const selling = val?.sellingPrice ?? '';
      const actual = val?.actualPrice ?? '';
      const qtyNum = Number(prev.quantity || 0);
      const unit = selling !== '' ? (Number(selling) || 0) : (Number(actual) || 0);
      const computedPrice = qtyNum > 0 ? String(unit * qtyNum) : '';
      return {
        ...prev,
        product: val || null,
        actualPrice: actual,
        salesPrice: selling,
        price: computedPrice,
      };
    });
    if (val) {
      setTimeout(() => {
        if (salesPriceInputRef.current) {
          salesPriceInputRef.current.focus();
        }
      }, 100);
    }
  };

  const handleSaveNewProduct = async (newProduct) => {
    await loadProducts();
    setForm((prev) => ({
        ...prev,
        product: newProduct,
        actualPrice: newProduct.actualPrice || '',
        salesPrice: newProduct.sellingPrice || '',
    }));
    setAddProductDialogOpen(false);
    requestAnimationFrame(() => {
        salesPriceInputRef.current?.focus();
    });
  };

  const setField = (name, value) => setForm((prev) => ({ ...prev, [name]: value }));

  const validate = () => {
    if (!selectedCustomer?.customerId) {
      alert('Select a customer');
      return false;
    }
    if (!form.product?.productId) {
      alert('Select a product');
      return false;
    }
    if (Number(form.price || 0) <= 0) {
      alert('Price must be greater than 0.');
      return false;
    }
    return true;
  };

  const save = async () => {
    if (!validate()) {
      setTimeout(() => {
        if (productInputRef.current) {
          try { productInputRef.current.focus(); } catch {}
        }
      }, 0);
      return;
    }
    try {
      setSaving(true);
      const payload = {
        productId: form.product.productId,
        customerId: selectedCustomer.customerId,
        quantity: form.quantity,
        salesPrice: form.salesPrice !== '' ? form.salesPrice : form.actualPrice,
        price: form.price,
        billTypeFlag: 'C', // Explicitly set flag for Customer Bill
      };

      const response = await apiService.customerBill.addItem(payload);

      const updatedSalesList = response.data.salesDetails || [];
      const updatedBillSummary = response.data.billSummary || {};

      // Reverse the list to show newest first
      const reversedList = Array.isArray(updatedSalesList) ? updatedSalesList.reverse() : [];
      setSalesList(reversedList);
      setTotalElements(reversedList.length);

      if (updatedBillSummary.billSummaryId) {
        setCurrentBillSummaryId(updatedBillSummary.billSummaryId);
        setTransactionOldBalance(String(updatedBillSummary.oldBalance || '0'));
      }

      await loadActiveBills();
      
      setForm({
        salesDetailId: undefined,
        product: null,
        productInput: '',
        actualPrice: '',
        salesPrice: '',
        quantity: '',
        price: '',
        originalPrice: 0,
      });
    } catch (e) {
      console.error('Failed to save:', e);
      alert('Failed to save');
    } finally {
      setSaving(false);
      setTimeout(() => {
        if (productInputRef.current) {
          try { productInputRef.current.focus(); } catch {}
        }
      }, 0);
    }
  };

  const saveAndPrint = async () => {
    if (!selectedCustomer?.customerId || !currentBillSummaryId) {
      alert('Select a customer and add at least one item before printing');
      return;
    }
    try {
      setIsPrinting(true);

      // Fire-and-forget the update to mark as printed
      apiService.collections.saveCollection({
        billSummaryId: currentBillSummaryId,
        isPrinted: 'Y',
      });

      // Use the bill data already in the component's state
      const billToPrint = {
        ...currentActiveBill,
        isPrinted: 'Y', // Manually set isPrinted to 'Y' for the print template
        cumulativeBalance: totals.balance, // Use the already calculated total from the UI
      };

      // Fetch the sales details for the print (this is the only blocking call)
      const salesDetailsForPrint = await apiService.salesDetails.getSalesByBillSummary(currentBillSummaryId, { billTypeFlag: 'C' });
      
      setSelectedBillToPrint(billToPrint);
      setSelectedBillDetailsToPrint(salesDetailsForPrint.data);
      setBillPrintDialogOpen(true);

      // Refresh the active bills list in the background
      loadActiveBills();
    } catch (error) {
      console.error('Error preparing bill for print:', error);
      alert('Failed to prepare bill for printing.');
    } finally {
      setIsPrinting(false);
    }
  };

  const editRow = (row) => {
    const prod = products.find(p => p.productId === row.productId) || null;
    setForm({
      salesDetailId: row.salesDetailId,
      product: prod,
      productInput: '',
      actualPrice: prod?.actualPrice ?? '',
      salesPrice: String(row.salesPrice ?? ''),
      quantity: String(row.quantity ?? ''),
      price: String(row.price ?? ''),
      originalPrice: Number(row.price || 0),
    });
    setActiveBills(prev => prev.map(bill => 
      bill.billSummaryId === row.billSummaryId
        ? { ...bill, billAmount: (Number(bill.billAmount) || 0) - (Number(row.price) || 0) }
        : bill
    ));
  };

  const cancelEdit = () => {
    setActiveBills(prev => prev.map(bill => 
      bill.billSummaryId === salesList.find(s => s.salesDetailId === form.salesDetailId)?.billSummaryId
        ? { ...bill, billAmount: (Number(bill.billAmount) || 0) + form.originalPrice }
        : bill
    ));
    setForm({
      salesDetailId: undefined,
      product: null,
      productInput: '',
      actualPrice: '',
      salesPrice: '',
      quantity: '',
      price: '',
      originalPrice: 0,
    });
  };

  const headerCellSx = {
    '& .MuiTableCell-head': {
      backgroundColor: '#e3f2fd',
      fontWeight: 700,
      borderBottom: '1px solid #bbdefb'
    }
  };

  const onPickActiveCustomer = (item) => {
    const found = (customers || []).find(c => c.customerId === item.customerId);
    if (found) {
      setSalesList([]); // Clear previous sales list immediately
      setPage(0); // **THE FIX**: Reset page to 0 on new selection
      setSelectedCustomer(found);
      setCurrentBillSummaryId(item.billSummaryId);
      setReceivedToday(String(item.receivedAmount || ''));
      setTransactionOldBalance(String(item.oldBalance || '0'));
      refreshBalance(found); // Always fetch overall balance for top card
      // Manually trigger fetch for the first page
      fetchSales(item.billSummaryId);
    }
  };

  const isCurrentBillPaid = currentActiveBill?.isPaid === 'Y';
  const isCurrentBillPrinted = currentActiveBill?.isPrinted === 'Y';

  const renderActiveBillsList = (isMobile = false) => (
    <Paper sx={{ p: 1, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Active Bills (Today)</Typography>
        {isMobile && (
          <IconButton onClick={() => setMobileOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        )}
      </Box>
      <List dense sx={{ flex: 1, overflow: 'auto' }}>
        {activeBills.map((ab) => (
          <React.Fragment key={ab.billSummaryId}>
            <ListItem
              button
              onClick={() => {
                onPickActiveCustomer(ab);
                if (isMobile) setMobileOpen(false);
              }}
              sx={{ '&:hover': { bgcolor: 'action.hover' } }}
            >
              <ListItemText
                primary={
                  <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                    {ab.customerName}
                    {ab.isPaid === 'Y' && (
                      <CheckCircleIcon sx={{ color: 'success.main', fontSize: '1rem', ml: 0.5 }} />
                    )}
                  </Box>
                }
                primaryTypographyProps={{ noWrap: true, title: ab.customerName }}
                secondary={`Amount: ${ab.grandTotal.toFixed(2)}`}
              />
            </ListItem>
            <Divider component="li" />
          </React.Fragment>
        ))}
        {activeBills.length === 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ p: 1, display: 'block' }}>
            No active bills
          </Typography>
        )}
      </List>
    </Paper>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 24px)' }}>
      <Paper sx={{ p: 2, mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 900, fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>Customer Bill</Typography>
            {activeBills.length > 0 && (
              <Button 
                variant="outlined" 
                size="small" 
                onClick={() => setMobileOpen(true)}
                sx={{ display: { xs: 'flex', md: 'none' }, minWidth: 'auto', px: 1 }}
              >
                Active Bills
              </Button>
            )}
          </Box>
          <Typography variant="caption" sx={{ opacity: 0.8 }}>{format(new Date(), 'PPP')}</Typography>
        </Box>
        
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 1.5, width: '100%', mb: 1.5 }}>
          <Box sx={{ display: 'flex', gap: 1, flex: { xs: '1 1 auto', md: '1 1 auto' }, width: '100%' }}>
            <Box sx={{ flexGrow: 1 }}>
            <Autocomplete
              options={customers || []}
              getOptionLabel={(o) => {
                if (!o) return '';
                if (o.isAddNew) return o.customerName;
                return `${o.customerId} - ${o.customerName} - ${o.customerNameTamil} - ${o.customerCity}`;
              }}
              value={selectedCustomer}
              onChange={onCustomerChange}
              inputValue={customerInput}
              filterOptions={(options, params) => {
                const filtered = filter(options, params);
                const { inputValue } = params;
                // Suggest the creation of a new value
                const isExisting = options.some((option) => inputValue === option.customerName);
                if (inputValue !== '' && !isExisting) {
                    filtered.push({
                        customerId: 'ADD_NEW', customerName: `Add "${inputValue}"`, customerNameTamil: '', customerCity: '', isAddNew: true
                    });
                }
                return filtered;
              }}
              onInputChange={(_e, v) => setCustomerInput(v)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Customer"
                  size="small"
                  autoFocus
                  inputRef={customerInputRef}
                  inputProps={{ ...params.inputProps, tabIndex: 1 }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      const getLabel = (opt) => opt ? `${opt.customerId} - ${opt.customerName} - ${opt.customerNameTamil} - ${opt.customerCity}` : '';
                      const firstMatch = (customers || []).find(opt =>
                        getLabel(opt).toLowerCase().includes((customerInput || '').toLowerCase())
                      );
                      if (firstMatch) {
                        e.preventDefault();
                        onCustomerChange(null, firstMatch);
                      }
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': { borderColor: '#1976d2' },
                    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#1976d2' },
                    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { 
                      borderColor: '#1976d2', 
                      borderWidth: '2px' 
                    }
                  }}
                  fullWidth
                />
              )}
              renderOption={(props, option) => {
                const { key, ...otherProps } = props;
                if (option.isAddNew) {
                  return (
                    <li key={key} {...otherProps} style={{ borderTop: '1px solid #eee', color: '#1976d2', fontWeight: 600 }}>
                      + {option.customerName}
                    </li>
                  );
                }
                return (
                  <li key={key} {...otherProps}>
                    {`${option.customerId} - ${option.customerName} - ${option.customerNameTamil} - ${option.customerCity}`}
                  </li>
                );
              }}
              isOptionEqualToValue={(o, v) => o.customerId === v?.customerId}
            />
            </Box>
            
            <Paper sx={{ 
              px: { xs: 1, md: 2 }, 
              height: 40, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              minWidth: { xs: 'auto', md: '200px' },
              flexShrink: 0
            }}>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#1976d2', whiteSpace: 'nowrap', fontSize: { xs: '1rem', md: '1.25rem' } }}>
                <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>Balance - </Box>
                {customerBalance}
              </Typography>
            </Paper>
          </Box>
        </Box>
      </Paper>

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, flexGrow: 1, minHeight: 0 }}>
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          {(!isMobile) && (
            <Paper sx={{ p: 2, mb: 1.5 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1.5 }}>Add Product</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', rowGap: { xs: 2, sm: 1.5 }, columnGap: { xs: 1, sm: 1.5 } }}>
                <Box sx={{ flex: { xs: '1 1 calc(100% - 100px)', md: '2 1 250px' } }}>
                  <Autocomplete
                    options={products || []}
                    getOptionLabel={(o) => {
                      if (!o) return '';
                      if (o.isAddNew) return o.productName;
                      return `${o.productId} - ${o.productName} - ${o.productNameTamil || ''}`;
                    }}
                    value={form.product}
                    onChange={onProductChange}
                    inputValue={form.productInput}
                    filterOptions={(options, params) => {
                      const filtered = filter(options, params);
                      const { inputValue } = params;
                      const isExisting = options.some((option) => inputValue === option.productName);
                      if (inputValue !== '' && !isExisting) {
                          filtered.push({
                              productId: 'ADD_NEW', productName: `Add "${inputValue}"`, isAddNew: true
                          });
                      }
                      return filtered;
                    }}
                    onInputChange={(_e, v) => setField('productInput', v)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Product"
                        size="small"
                        inputRef={productInputRef}
                        inputProps={{ ...params.inputProps, tabIndex: 2 }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            const labelFor = (opt) => opt ? `${opt.productId} - ${opt.productName} - ${opt.productNameTamil || ''}` : '';
                            const firstMatch = (products || []).find(opt =>
                              labelFor(opt).toLowerCase().includes(String(form.productInput || '').toLowerCase())
                            );
                            if (firstMatch) {
                              e.preventDefault();
                              onProductChange(null, firstMatch);
                            }
                          }
                        }}
                        fullWidth
                      />
                    )}
                    renderOption={(props, option) => {
                      const { key, ...otherProps } = props;
                      if (option.isAddNew) {
                        return (
                          <li key={key} {...otherProps} style={{ borderTop: '1px solid #eee', color: '#1976d2', fontWeight: 600 }}>
                            + {option.productName}
                          </li>
                        );
                      }
                      return (
                        <li key={key} {...otherProps}>
                          {`${option.productId} - ${option.productName} - ${option.productNameTamil || ''}`}
                        </li>
                      );
                    }}
                    isOptionEqualToValue={(o, v) => o.productId === v?.productId}
                  />
                </Box>

                <Box sx={{ flex: { xs: '0 0 90px', sm: '1 1 100px' } }}>
                  <TextField
                    label="Actual Price"
                    size="small"
                    value={form.actualPrice}
                    InputProps={{ readOnly: true }}
                    fullWidth
                    inputProps={{ tabIndex: -1 }}
                  />
                </Box>

                <Box sx={{ flex: { xs: '1 1 85px', sm: '1 1 100px' } }}>
                  <TextField
                    label="Sales Price"
                    size="small"
                    inputRef={salesPriceInputRef}
                    value={form.salesPrice}
                    onChange={(e) => {
                      const val = e.target.value;
                      setForm(prev => {
                        const qtyNum = Number(prev.quantity || 0);
                        const unit = val === '' ? (Number(prev.actualPrice || 0) || 0) : (Number(val) || 0);
                        const computed = qtyNum > 0 ? String(unit * qtyNum) : '';
                        return { ...prev, salesPrice: val, price: computed };
                      });
                    }}
                    type="number"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if (quantityInputRef.current) {
                          quantityInputRef.current.focus();
                        }
                      }
                    }}
                    inputProps={{ tabIndex: 3 }}
                    fullWidth
                  />
                </Box>

                <Box sx={{ flex: { xs: '1 1 85px', sm: '1 1 100px' } }}>
                  <TextField
                    label="KG / Count"
                    size="small"
                    inputRef={quantityInputRef}
                    value={form.quantity}
                    onChange={(e) => {
                      const val = e.target.value;
                      setForm(prev => {
                        const unit = prev.salesPrice !== '' ? (Number(prev.salesPrice) || 0) : (Number(prev.actualPrice || 0) || 0);
                        const computed = val === '' ? '' : String(unit * (Number(val) || 0));
                        return { ...prev, quantity: val, price: computed };
                      });
                    }}
                    type="number"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        save();
                      }
                    }}
                    inputProps={{ tabIndex: 4 }}
                    fullWidth
                  />
                </Box>

                <Box sx={{ flex: { xs: '1 1 85px', sm: '1 1 100px' } }}>
                  <TextField
                    label="Price"
                    size="small"
                    value={form.price}
                    InputProps={{ readOnly: true }}
                    type="number"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        save();
                      }
                    }}
                    inputProps={{ tabIndex: 5 }}
                    fullWidth
                  />
                </Box>

                <Box sx={{ flex: { xs: '1 1 auto', sm: '0 0 100px' }, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={() => save()}
                    disabled={saving}
                    fullWidth
                    sx={{ height: '40px' }}
                  >
                    {form.salesDetailId ? 'Update' : 'Save'}
                  </Button>
                  {form.salesDetailId && (
                    <IconButton onClick={cancelEdit} sx={{ ml: 1 }}>
                      <CancelIcon />
                    </IconButton>
                  )}
                </Box>
              </Box>
            </Paper>
          )}

          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {isMobile ? (
              <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0, pb: 1 }}>
                <Paper sx={{ p: 2, mb: 1.5 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1.5 }}>Add Product</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', rowGap: { xs: 2, sm: 1.5 }, columnGap: { xs: 1, sm: 1.5 } }}>
                    <Box sx={{ flex: { xs: '1 1 calc(100% - 100px)', md: '2 1 250px' } }}>
                      <Autocomplete
                        options={products || []}
                        getOptionLabel={(o) => {
                          if (!o) return '';
                          if (o.isAddNew) return o.productName;
                          return `${o.productId} - ${o.productName} - ${o.productNameTamil || ''}`;
                        }}
                        value={form.product}
                        onChange={onProductChange}
                        inputValue={form.productInput}
                        filterOptions={(options, params) => {
                          const filtered = filter(options, params);
                          filtered.push({
                            productId: 'ADD_NEW', productName: 'Add New Product', productNameTamil: '', isAddNew: true
                          });
                          return filtered;
                        }}
                        onInputChange={(_e, v) => setField('productInput', v)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Product"
                            size="small"
                            inputRef={productInputRef}
                            inputProps={{ ...params.inputProps, tabIndex: 2 }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                const labelFor = (opt) => opt ? `${opt.productId} - ${opt.productName} - ${opt.productNameTamil || ''}` : '';
                                const firstMatch = (products || []).find(opt =>
                                  labelFor(opt).toLowerCase().includes(String(form.productInput || '').toLowerCase())
                                );
                                if (firstMatch) {
                                  e.preventDefault();
                                  onProductChange(null, firstMatch);
                                }
                              }
                            }}
                            fullWidth
                          />
                        )}
                        renderOption={(props, option) => {
                          const { key, ...otherProps } = props;
                          if (option.isAddNew) {
                            return (
                              <li key={key} {...otherProps} style={{ borderTop: '1px solid #eee', color: '#1976d2', fontWeight: 600 }}>
                                + {option.productName}
                              </li>
                            );
                          }
                          return (
                            <li key={key} {...otherProps}>
                              {`${option.productId} - ${option.productName} - ${option.productNameTamil || ''}`}
                            </li>
                          );
                        }}
                        isOptionEqualToValue={(o, v) => o.productId === v?.productId}
                      />
                    </Box>

                    <Box sx={{ flex: { xs: '0 0 90px', sm: '1 1 100px' } }}>
                      <TextField
                        label="Actual Price"
                        size="small"
                        value={form.actualPrice}
                        InputProps={{ readOnly: true }}
                        fullWidth
                        inputProps={{ tabIndex: -1 }}
                      />
                    </Box>

                    <Box sx={{ flex: { xs: '1 1 85px', sm: '1 1 100px' } }}>
                      <TextField
                        label="Sales Price"
                        size="small"
                        inputRef={salesPriceInputRef}
                        value={form.salesPrice}
                        onChange={(e) => {
                          const val = e.target.value;
                          setForm(prev => {
                            const qtyNum = Number(prev.quantity || 0);
                            const unit = val === '' ? (Number(prev.actualPrice || 0) || 0) : (Number(val) || 0);
                            const computed = qtyNum > 0 ? String(unit * qtyNum) : '';
                            return { ...prev, salesPrice: val, price: computed };
                          });
                        }}
                        type="number"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            if (quantityInputRef.current) {
                              quantityInputRef.current.focus();
                            }
                          }
                        }}
                        inputProps={{ tabIndex: 3 }}
                        fullWidth
                      />
                    </Box>

                    <Box sx={{ flex: { xs: '1 1 85px', sm: '1 1 100px' } }}>
                      <TextField
                        label="KG / Count"
                        size="small"
                        inputRef={quantityInputRef}
                        value={form.quantity}
                        onChange={(e) => {
                          const val = e.target.value;
                          setForm(prev => {
                            const unit = prev.salesPrice !== '' ? (Number(prev.salesPrice) || 0) : (Number(prev.actualPrice || 0) || 0);
                            const computed = val === '' ? '' : String(unit * (Number(val) || 0));
                            return { ...prev, quantity: val, price: computed };
                          });
                        }}
                        type="number"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            save();
                          }
                        }}
                        inputProps={{ tabIndex: 4 }}
                        fullWidth
                      />
                    </Box>

                    <Box sx={{ flex: { xs: '1 1 85px', sm: '1 1 100px' } }}>
                      <TextField
                        label="Price"
                        size="small"
                        value={form.price}
                        InputProps={{ readOnly: true }}
                        type="number"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            save();
                          }
                        }}
                        inputProps={{ tabIndex: 5 }}
                        fullWidth
                      />
                    </Box>

                    <Box sx={{ flex: { xs: '1 1 auto', sm: '0 0 100px' }, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={() => save()}
                        disabled={saving}
                        fullWidth
                        sx={{ height: '40px' }}
                      >
                        {form.salesDetailId ? 'Update' : 'Save'}
                      </Button>
                      {form.salesDetailId && (
                        <IconButton onClick={cancelEdit} sx={{ ml: 1 }}>
                          <CancelIcon />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                </Paper>
                {salesList.map((row, index) => {
                  const prod = (products || []).find(p => p.productId === row.productId);
                  const productName = prod?.productNameTamil || prod?.productName || '';
                  return (
                    <Paper key={row.salesDetailId} sx={{ p: 0.75, mb: 0.5, mx: 0.5, display: 'flex', alignItems: 'center' }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }} noWrap>
                          {index + 1}. {productName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.1 }}>
                          {row.salesPrice || prod?.actualPrice || 'N/A'} x {row.quantity} = <b>{row.price}</b>
                        </Typography>
                      </Box>

                      {!isCurrentBillPaid ? (
                        <Box sx={{ display: 'flex', flexShrink: 0, ml: 0.5 }}>
                          <IconButton size="small" onClick={() => editRow(row)} sx={{ p: 0.5 }}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                              {canDelete && (
                            <IconButton size="small" color="error" onClick={() => deleteRow(row)} sx={{ p: 0.5 }}>
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Box>
                          ) : (
                        <Box sx={{ ml: 1 }}>
                               <CheckCircleIcon color="success" fontSize="small" />
                            </Box>
                          )}
                    </Paper>
                  );
                })}
                {salesList.length === 0 && (
                  <Typography variant="body2" align="center" color="text.secondary" sx={{ mt: 4 }}>
                    No items added yet.
                  </Typography>
                )}
              </Box>
            ) : (
              <TableContainer component={Paper} sx={{ flex: 1, minHeight: 0 }}>
                <Table stickyHeader size="small">
                  {salesList.length > 0 && (
                    <TableHead sx={headerCellSx}>
                      <TableRow>
                        <TableCell>S NO</TableCell>
                        <TableCell>Product</TableCell>
                        <TableCell align="right">Sales Price</TableCell>
                        <TableCell align="right">KG/Count</TableCell>
                        <TableCell align="right">Price</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                  )}
                  <TableBody>
                    {salesList.map((row, index) => {
                      const prod = (products || []).find(p => p.productId === row.productId);
                      const productName = prod?.productNameTamil || '';
                      return (
                        <TableRow key={row.salesDetailId} hover>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>{productName}</TableCell>
                          <TableCell align="right">{row.salesPrice || prod?.actualPrice || 'N/A'}</TableCell>
                          <TableCell align="right">{row.quantity}</TableCell>
                          <TableCell align="right">{row.price}</TableCell>
                          <TableCell>
                            {!isCurrentBillPaid && (
                              <>
                                <IconButton size="small" onClick={() => editRow(row)}><EditIcon /></IconButton>
                                {canDelete && (
                                  <IconButton size="small" color="error" onClick={() => deleteRow(row)}><CloseIcon /></IconButton>
                                )}
                              </>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {salesList.length > 0 && (
              <Paper sx={{ p: { xs: 1, md: 1.5 }, mt: 1, flexShrink: 0, borderTop: { xs: '1px solid #e0e0e0', md: 'none' } }}>
                {/* Desktop View */}
                <Box sx={{ display: { xs: 'none', md: 'flex' }, flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
                  {!isCurrentBillPaid ? (
                    <>
                      <Box sx={{ flex: '1 1 130px' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Page Total: {totals.total.toFixed(2)}</Typography>
                      </Box>
                      <Box sx={{ flex: '1 1 130px' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Old Balance: {totals.oldBal.toFixed(2)}</Typography>
                      </Box>
                      <Box sx={{ flex: '1 1 130px' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Grand Total: {totals.grandTotal.toFixed(2)}</Typography>
                      </Box>
                      <Box sx={{ flex: '1 1 100px' }}>
                        <TextField
                          label="Received"
                          size="small"
                          type="number"
                          value={receivedToday}
                          onChange={(e) => setReceivedToday(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              saveReceived();
                            }
                          }}
                          fullWidth
                        />
                      </Box>
                      <Box sx={{ flex: '1 1 100px' }}>
                        <Button
                          variant="contained"
                          onClick={saveReceived}
                          fullWidth
                          size="small"
                        >
                          Save
                        </Button>
                      </Box>
                      <Box sx={{ flex: '1 1 100px', textAlign: 'right' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1976d2' }}>
                          Balance: {totals.balance.toFixed(2)}
                        </Typography>
                      </Box>
                    </>
                  ) : (
                    <Box sx={{ flex: 1, display: 'flex', justifyContent: 'space-around', alignItems: 'center' }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                        Old Balance: {Number(currentActiveBill?.oldBalance || 0).toFixed(2)}
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.main' }}>
                        Received Amount: {Number(currentActiveBill?.receivedAmount || 0).toFixed(2)}
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ flex: '0 0 auto', ml: 'auto' }}>
                    <Button
                      variant="contained"
                      color={isCurrentBillPrinted ? 'info' : 'success'}
                      startIcon={<PrintIcon />}
                      onClick={saveAndPrint}
                      disabled={isPrinting}
                      size="small"
                    >
                      Print
                    </Button>
                  </Box>
                </Box>

                {/* Mobile View */}
                <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 1, justifyContent: 'space-between' }}>
                  {!isCurrentBillPaid ? (
                    <>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                         <Typography variant="caption" sx={{ fontWeight: 700, lineHeight: 1.1 }}>Gr: {totals.grandTotal.toFixed(2)}</Typography>
                         <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', lineHeight: 1.1 }}>Old: {totals.oldBal.toFixed(2)}</Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <TextField
                          placeholder="Rcvd"
                          size="small"
                          type="number"
                          value={receivedToday}
                          onChange={(e) => setReceivedToday(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              saveReceived();
                            }
                          }}
                          sx={{ width: 70, '& .MuiInputBase-input': { p: '4px 8px', fontSize: '0.9rem' } }}
                        />
                        <IconButton
                          onClick={saveReceived}
                          color="primary"
                          size="small"
                          sx={{ bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' }, p: 0.5, borderRadius: 1 }}
                        >
                          <SaveIcon fontSize="small" />
                        </IconButton>
                      </Box>

                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                           <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', lineHeight: 1.1 }}>Bal</Typography>
                           <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1976d2', lineHeight: 1.1 }}>{totals.balance.toFixed(2)}</Typography>
                      </Box>
                      
                      <IconButton
                        color={isCurrentBillPrinted ? 'info' : 'success'}
                        onClick={saveAndPrint}
                        disabled={isPrinting}
                        size="small"
                        sx={{ border: '1px solid', borderRadius: 1, p: 0.5, borderColor: 'divider' }}
                      >
                        <PrintIcon fontSize="small" />
                      </IconButton>
                    </>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                       <Box>
                          <Typography variant="caption" display="block">Old Bal: {Number(currentActiveBill?.oldBalance || 0).toFixed(2)}</Typography>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'success.main' }}>
                            Rcvd: {Number(currentActiveBill?.receivedAmount || 0).toFixed(2)}
                          </Typography>
                       </Box>
                       <Button
                          variant="contained"
                          color={isCurrentBillPrinted ? 'info' : 'success'}
                          startIcon={<PrintIcon />}
                          onClick={saveAndPrint}
                          disabled={isPrinting}
                          size="small"
                        >
                          Print
                        </Button>
                    </Box>
                  )}
                </Box>
              </Paper>
            )}
          </Box>
        </Box>

        <Box sx={{ width: { xs: '100%', md: 300 }, flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
          {renderActiveBillsList(false)}
        </Box>

        <Drawer
          anchor="right"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{ '& .MuiDrawer-paper': { width: '80%', maxWidth: 300 } }}
        >
          {renderActiveBillsList(true)}
        </Drawer>
        </Box>
        {selectedBillToPrint && (
          <BillPrintDialog
            open={isBillPrintDialogOpen}
            onClose={() => setBillPrintDialogOpen(false)}
            bill={selectedBillToPrint}
            salesDetails={selectedBillDetailsToPrint}
          />
        )}

        <AddCustomerDialog
          open={addCustomerDialogOpen}
          onClose={() => setAddCustomerDialogOpen(false)}
          onSaveSuccess={handleSaveNewCustomer}
        />
        <AddProductDialog
          open={addProductDialogOpen}
          onClose={() => setAddProductDialogOpen(false)}
          onSaveSuccess={handleSaveNewProduct}
        />
    </Box>
  );
};

export default CustomerBill;
